import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, ToggleButton, ToggleButtonGroup,
  TextField, MenuItem, Grid, Card, CardContent, CircularProgress,
  Tooltip as MuiTooltip,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { DataGrid } from '../../components/common/datagrid/ResponsiveDataGrid';
import BreadcrumbsContainer from '../../containers/common/breadcurmbs/BreadcrumbsContainer';
import KpiReportTemplate from '../../components/kpimng/KpiReportTemplate';
import * as kpiApi from '../../lib/api/kpiReport';

// 2026-10-27 소유자 결정(옵션 B): "고장"은 지속시간이 문턱(분) 이상인 알람만이다. ALARM ON -> 짝이
// 되는 OFF(또는 아직 진행중이면 조회 시점까지 경과시간)가 이 문턱 미만이면 "짧은 알람"으로 따로 세고
// (shortAlarmCnt) 고장(failureCnt)·MTBF·MTTR 집계에서는 뺀다.
// [버그수정, Fable 사전검토 3-5] 이 문턱을 화면에 "5분"으로 하드코딩해 두면 운영에서
// eqp.failure.min-alarm-minutes 설정을 바꿔도 화면 문구만 안 맞게 된다 - API 응답의
// minAlarmMinutes(KpiReportDto, eqp.failure.min-alarm-minutes 그대로)를 그대로 쓴다. 레거시
// (EQP_LOG_INF 수동 입력) 데이터만 있어 minAlarmMinutes가 없을 때만 기본값으로 보여준다.
const DEFAULT_FAILURE_MIN_ALARM_MINUTES = 5; // EqpReliabilityCalculator.DEFAULT_MIN_ALARM_MINUTES와 동일(폴백 전용)
const buildFailureRuleText = (minAlarmMinutes) =>
  `${minAlarmMinutes}분 이상 지속된 알람만 "고장"으로 집계합니다. ` +
  `${minAlarmMinutes}분 미만의 짧은 알람(순간 OT 등)은 고장 집계·MTBF·MTTR에서 제외하고, ` +
  '전체 알람 건수에만 포함해 참고용으로 같이 보여줍니다.';

const fmt = (v, d = 1) => (v != null ? Number(v).toFixed(d) : '-');

// KpiDashboardPage(통합 대시보드) 의 생산 탭이 이 fetchFn/chartConfig/columns/summaryFn 을
// 그대로 import 해서 쓴다 - 같은 코드를 재사용해 화면마다 값이 갈리지 않게 한다.

// 생산(targetQty/producedQty/workHours/throughput)과 공정불량률(processOutputQty/defectQty/
// processDefectRate/goodRate)은 서로 다른 쿼리·다른 모집단이다(selectProductionKpi 는
// PLAN_STAT_CD IN ('PRODUCED','SHIPPED') 로 좁고, selectProcessDefectKpi 는 상태 제한이 없어
// 진행중·QC_FAILED 계획도 포함한다 - 공정불량률의 정본은 후자다). 그래서 API 를 두 개 불러
// (kpiYm 또는 kpiYear, productId) 키로 바깥 조인(full outer join)해 한 리스트로 합친다.
// 어느 한쪽에만 있는 행도 버리지 않는다 - 버리면 요약 합계가 다시 축소되어 같은 버그가 재발한다.
//
// 공정불량률/양품률 관련 컬럼·차트·요약카드는 품질 리포트(KpiQualityPage)와 중복이라 이 화면
// 에서는 걷어냈다(작업 지시) - 하지만 조인 자체는 그대로 둔다. KpiDashboardPage 의 대시보드 탭이
// 이 fetchFn 이 돌려주는 병합 리스트의 defectQty 필드를 월별 추이 차트에 그대로 쓰고 있어서,
// 조인을 걷어내면 그 화면이 깨진다. processOutputQty/processDefectRate/goodRate 필드는 병합
// 결과에 남아있지만 이 화면·다른 화면 어디서도 더 읽지 않는다(grep 확인) - 그래도 조인 로직
// 자체를 손대는 건 이번 작업 범위 밖이라 필드만 남겨두고 표시만 뺐다.
const keyOf = (r) => `${r.kpiYm || r.kpiYear || ''}__${r.productId || ''}`;

export const fetchFn = async (params) => {
  const [pRes, pdRes] = await Promise.all([
    kpiApi.getProductionKpi(params),
    kpiApi.getProcessDefectKpi(params),
  ]);
  const prodList = pRes.data?.list || [];
  const pdList   = pdRes.data?.list || [];

  const merged = new Map();
  prodList.forEach((r) => {
    merged.set(keyOf(r), {
      kpiYm: r.kpiYm, kpiYear: r.kpiYear, productId: r.productId, productNm: r.productNm,
      targetQty: r.targetQty, producedQty: r.producedQty,
      workHours: r.workHours, throughput: r.throughput,
      processOutputQty: null, defectQty: null, processDefectRate: null, goodRate: null,
    });
  });
  pdList.forEach((r) => {
    const key = keyOf(r);
    const base = merged.get(key) || {
      kpiYm: r.kpiYm, kpiYear: r.kpiYear, productId: r.productId, productNm: r.productNm,
      targetQty: null, producedQty: null, workHours: null, throughput: null,
    };
    merged.set(key, {
      ...base,
      // defectQty 는 selectProcessDefectKpi(정본)의 processDefectQty 를 그대로 쓴다 -
      // selectProductionKpi 자체의 defectQty(생산 KPI 폐기수량 등, 다른 의미)는 버린다.
      processOutputQty: r.processOutputQty,
      defectQty: r.processDefectQty,
      processDefectRate: r.processDefectRate,
      goodRate: r.goodRate,
    });
  });
  return { data: { list: Array.from(merged.values()) } };
};

export const chartConfig = {
  type: 'bar',
  bars: [
    { key: 'producedQty', name: '생산완료수량', color: '#1976d2' },
    { key: 'targetQty',   name: '목표수량', color: '#43a047' },
  ],
};

export const columns = [
  { field: 'kpiYm',      headerName: '년월',   width: 90 },
  { field: 'kpiYear',    headerName: '연도',   width: 70 },
  { field: 'productNm',  headerName: '제품명', minWidth: 160, flex: 1 },
  { field: 'targetQty',  headerName: '목표수량',  width: 100, valueFormatter: (v) => fmt(v, 0) },
  { field: 'producedQty',headerName: '생산완료수량',  width: 120, valueFormatter: (v) => fmt(v, 0) },
  { field: 'workHours',  headerName: '작업시간(h)', width: 110, valueFormatter: (v) => fmt(v) },
  { field: 'throughput', headerName: 'Throughput', width: 110, valueFormatter: (v) => fmt(v) },
];

export const summaryFn = (list) => {
  const totalProduced      = list.reduce((s, r) => s + (Number(r.producedQty)      || 0), 0);
  const totalHours         = list.reduce((s, r) => s + (Number(r.workHours)        || 0), 0);
  const throughput = totalHours > 0 ? (totalProduced / totalHours).toFixed(2) : '-';
  return [
    { label: '총 생산완료수량', value: totalProduced.toLocaleString(), unit: '개', color: '#1976d2' },
    { label: '총 작업시간', value: totalHours.toFixed(1),           unit: 'h',  color: '#555' },
    { label: 'Throughput', value: throughput,                       unit: '/h', color: '#7b1fa2' },
  ];
};

// ── 기간 선택 / 요약 카드 (설비 가동 섹션용) ─────────────────────────
// KpiCostContent(원가 리포트)의 설비 KPI 탭에 있던 로컬 컴포넌트를 그대로 옮겨왔다 -
// KpiReportTemplate 는 기간(kpiYm/kpiYear)별 집계만 지원해 장비별 집계(아래 EqpKpiSection)에는
// 쓸 수 없어서 별도로 둔다.
// export 하는 이유: 원가 리포트(KpiCostContent)의 매입 KPI 섹션도 장비별 집계처럼 기간별
// 필터링이 필요한데 KpiReportTemplate 를 그대로 못 쓴다(요약카드 색상·절감률 null 안내 같은
// 커스텀 렌더링이 필요해서) - 여기서 같은 기간 선택 UI를 재사용한다.
export const PeriodSelector = ({ mode, setMode, year, setYear }) => {
  const cur = new Date().getFullYear();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
      <ToggleButtonGroup size="small" value={mode} exclusive onChange={(_, v) => v && setMode(v)}>
        <ToggleButton value="monthly">월별</ToggleButton>
        <ToggleButton value="yearly">연도별 (2개년)</ToggleButton>
      </ToggleButtonGroup>
      {mode === 'monthly' && (
        <TextField
          select size="small" label="연도" value={year}
          onChange={(e) => setYear(e.target.value)} sx={{ width: 110 }}
        >
          {[cur, cur - 1].map((y) => (
            <MenuItem key={y} value={String(y)}>{y}년</MenuItem>
          ))}
        </TextField>
      )}
    </Box>
  );
};

// sub: 값 아래 보조 설명 한 줄(예: "전체 알람 n건 (5분 미만 n건 제외)"). help: 라벨 옆에 작은 ⓘ 아이콘을
// 붙여 hover 시 규칙을 설명하는 텍스트(MainDashboardPage의 MetricHelp와 같은 패턴 - HelpOutlineIcon +
// MuiTooltip).
const SummaryCards = ({ items }) => (
  <Grid container spacing={1.5} sx={{ mb: 2 }}>
    {items.map(({ label, value, unit, color, sub, help }) => (
      <Grid item xs={6} sm={4} md={2} key={label}>
        <Card variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {label}
              {help && (
                <MuiTooltip arrow placement="top" title={help}>
                  <HelpOutlineIcon
                    sx={{
                      fontSize: 13, ml: 0.3, verticalAlign: 'middle', color: 'text.disabled', cursor: 'help',
                      '&:hover': { color: 'text.secondary' },
                    }}
                  />
                </MuiTooltip>
              )}
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color }}>
              {value}{unit}
            </Typography>
            {sub && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {sub}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// ── 설비 가동 섹션 ────────────────────────────────────────────────
// 원가 리포트(KpiCostContent)에 있던 "설비 KPI" 탭(EqpKpiTab)을 그대로 옮겨왔다(작업 지시) -
// GET /kpi/eqp-cost 호출·계산식은 손대지 않았다. 생산 리포트 하단에 별도 섹션으로 붙인다.
// 실제로 렌더되는 곳은 KpiDashboardPage 의 생산 탭이다(라우트가 /kpimng 하나뿐이고
// 이 파일의 default export 는 연결돼 있지 않다). 그래서 export 해서 거기서 가져다 쓴다.
export const EqpKpiSection = () => {
  const currentYear = String(new Date().getFullYear());
  const [mode, setMode] = useState('monthly');
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = mode === 'monthly' ? { searchYear: year } : { kpiYear: 'Y' };
      const res = await kpiApi.getEqpCostKpi(params);
      setData(res.data.list || []);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [mode, year]);

  useEffect(() => { load(); }, [load]);

  // [버그수정, Fable 사전검토 3-5] 캡션/열 헤더 툴팁/요약카드가 전부 이 값을 같이 쓴다 - 서버
  // (eqp.failure.min-alarm-minutes)가 바뀌면 화면 문구도 같이 바뀌게.
  const minAlarmMinutes = React.useMemo(
    () => data.find((r) => r.minAlarmMinutes != null)?.minAlarmMinutes
      ?? DEFAULT_FAILURE_MIN_ALARM_MINUTES,
    [data],
  );
  const failureRuleText = React.useMemo(
    () => buildFailureRuleText(minAlarmMinutes),
    [minAlarmMinutes],
  );

  const chartData = React.useMemo(() => {
    const map = {};
    data.forEach((row) => {
      const key = row.eqpNm || row.eqpUuid;
      if (!key) return;
      if (!map[key]) map[key] = { eqpNm: key, operRateSum: 0, mtbfSum: 0, mttrSum: 0, cnt: 0 };
      map[key].operRateSum += Number(row.operRate) || 0;
      map[key].mtbfSum    += Number(row.mtbf)     || 0;
      map[key].mttrSum    += Number(row.mttr)     || 0;
      map[key].cnt        += 1;
    });
    return Object.values(map).map((r) => ({
      eqpNm:    r.eqpNm,
      operRate: r.cnt ? +(r.operRateSum / r.cnt).toFixed(1) : 0,
      mtbf:     r.cnt ? +(r.mtbfSum    / r.cnt).toFixed(2) : 0,
      mttr:     r.cnt ? +(r.mttrSum    / r.cnt).toFixed(2) : 0,
    }));
  }, [data]);

  const summaryItems = React.useMemo(() => {
    if (!data.length) return [];
    const cnt        = data.length;
    const avgOper    = (data.reduce((s, r) => s + (Number(r.operRate) || 0), 0) / cnt).toFixed(1);
    const avgMtbf    = (data.reduce((s, r) => s + (Number(r.mtbf)     || 0), 0) / cnt).toFixed(2);
    const avgMttr    = (data.reduce((s, r) => s + (Number(r.mttr)     || 0), 0) / cnt).toFixed(2);
    const totalFail  = data.reduce((s, r) => s + (Number(r.failureCnt) || 0), 0);
    // totalAlarmCnt/shortAlarmCnt는 에이전트 데이터(dataSrc=AGENT)에만 있다 - 레거시(EQP_LOG_INF
    // 수동 입력) 행은 null이라 reduce에서 자연히 0으로 빠진다(전체 알람 = 고장 건수와 같아진다).
    const totalAlarm = data.reduce((s, r) => s + (Number(r.totalAlarmCnt) || 0), 0);
    const totalShort = data.reduce((s, r) => s + (Number(r.shortAlarmCnt) || 0), 0);
    return [
      { label: '평균 가동률',   value: avgOper,              unit: '%',  color: '#1976d2' },
      { label: '평균 MTBF',    value: avgMtbf,              unit: 'h',  color: '#388e3c' },
      { label: '평균 MTTR',    value: avgMttr,              unit: 'h',  color: '#f57c00' },
      {
        label: '고장',
        value: totalFail.toLocaleString(),
        unit: '건',
        color: '#c62828',
        sub: `전체 알람 ${totalAlarm.toLocaleString()}건 (${minAlarmMinutes}분 미만 ${totalShort.toLocaleString()}건 제외)`,
        help: failureRuleText,
      },
    ];
  }, [data, minAlarmMinutes, failureRuleText]);

  // 2026-10-23 "기준시간 가동률" 계획: planHr은 이제 "기준시간"(계획 가동시간)이고, 가동률은 기준시간
  // 밖 가동도 포함해서 재므로 100%를 넘을 수 있다(음수 아님) - extraOperHr(기준시간 밖 가동)을 따로 보여준다.
  // planHr=0(그 기간이 전부 휴일 등)이면 operRate는 "해당없음". dataSrc는 AGENT(에이전트 데이터)/
  // LEGACY(EQP_LOG_INF 수동 입력 폴백) 중 어느 쪽으로 계산됐는지 알려준다.
  // 2026-10-27 "고장" 판정 변경(옵션 B): failureCnt는 지속시간 5분 이상인 알람만 - totalAlarmCnt(전체
  // 알람, 길이 무관)를 바로 옆에 같이 보여줘 "짧은 알람은 왜 빠졌는지" 비교할 수 있게 한다. 레거시
  // (EQP_LOG_INF 수동 입력) 행은 totalAlarmCnt가 없다(null) - '-'로 표시.
  const eqpColumns = [
    { field: 'kpiYm',        headerName: '년월',     width: 90 },
    { field: 'kpiYear',      headerName: '연도',     width: 70 },
    { field: 'eqpNm',        headerName: '장비명',   minWidth: 140, flex: 1 },
    { field: 'totalOperHr',  headerName: '가동시간(h)', width: 110, valueFormatter: (v) => fmt(v) },
    { field: 'planHr',       headerName: '기준시간(h)', width: 110, valueFormatter: (v) => v != null ? fmt(v) : '-' },
    {
      field: 'operRate', headerName: '가동률(%)', width: 100,
      valueFormatter: (v) => (v != null ? `${fmt(v)}%` : '해당없음'),
    },
    { field: 'extraOperHr',  headerName: '기준시간 밖 가동(h)', width: 140, valueFormatter: (v) => v != null ? fmt(v) : '-' },
    {
      field: 'failureCnt', headerName: '고장 건수', width: 90, valueFormatter: (v) => v ?? '-',
      renderHeader: () => (
        <MuiTooltip arrow title={failureRuleText}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            고장 건수
            <HelpOutlineIcon sx={{ fontSize: 13, ml: 0.3, color: 'text.disabled', cursor: 'help' }} />
          </Box>
        </MuiTooltip>
      ),
    },
    { field: 'totalAlarmCnt', headerName: '전체 알람', width: 90, valueFormatter: (v) => v ?? '-' },
    { field: 'mtbf',         headerName: 'MTBF(h)',  width: 100, valueFormatter: (v) => fmt(v, 2) },
    { field: 'repairCnt',    headerName: '수리 건수', width: 90, valueFormatter: (v) => v ?? '-' },
    { field: 'totalRepairHr',headerName: '수리시간(h)', width: 110, valueFormatter: (v) => fmt(v) },
    { field: 'mttr',         headerName: 'MTTR(h)',  width: 100, valueFormatter: (v) => fmt(v, 2) },
    {
      field: 'dataSrc', headerName: '데이터 출처', width: 110,
      valueFormatter: (v) => (v === 'LEGACY' ? '수동 입력' : v === 'AGENT' ? '에이전트' : '-'),
    },
  ];

  return (
    <Box>
      {/* 제목을 섹션 안에 둔다. 밖에 두면 이 컴포넌트를 가져다 쓰는 쪽(KpiDashboardPage 생산 탭)에
          제목이 안 따라간다 — 실제로 그렇게 돼서 화면에 안 보였다. */}
      <Typography variant="h6" fontWeight={700}>설비 가동</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        ※ {failureRuleText}
      </Typography>
      <PeriodSelector mode={mode} setMode={setMode} year={year} setYear={setYear} />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <SummaryCards items={summaryItems} />
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                장비별 평균 가동률 / MTBF / MTTR
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="eqpNm" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="operRate" name="가동률(%)"  fill="#1976d2" barSize={16} />
                  <Bar dataKey="mtbf"     name="MTBF(h)"   fill="#388e3c" barSize={16} />
                  <Bar dataKey="mttr"     name="MTTR(h)"   fill="#f57c00" barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <DataGrid
            rows={data.map((r, i) => ({ id: i, ...r }))}
            columns={eqpColumns}
            autoHeight
            disableColumnMenu
            pageSizeOptions={[20, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
            density="compact"
          />
        </>
      )}
    </Box>
  );
};

const KpiProductionPage = () => (
  <Box sx={{ p: 3 }}>
    <BreadcrumbsContainer title="목표관리/KPI 생산 리포트" icon="goal" />
    <KpiReportTemplate
      title="생산 KPI"
      subtitle="※ 불량수량·양품률(검사공정 기준)은 품질 리포트로 이동했습니다 - 여기서는 생산 지표만 표시합니다."
      fetchFn={fetchFn}
      chartConfig={chartConfig}
      columns={columns}
      summaryFn={summaryFn}
    />

    {/* 설비 가동 - 원가 리포트에 있던 설비 KPI 섹션을 그대로 옮겨왔다(작업 지시) */}
    <Box sx={{ mt: 5 }}>
      <EqpKpiSection />
    </Box>
  </Box>
);

export default KpiProductionPage;
